/**
 * useAuditSSE Composable
 * Connects SSE service to audit store for real-time event handling
 */
import { onMounted, onUnmounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '../stores/auth.store.js';
import { useAuditStore } from '../stores/audit.store.js';
import { isUnauthorizedError } from '../services/api-error.service.js';
import { redirectToLogin } from '../services/auth-redirect.service.js';
import { sseService } from '../services/sse.service.js';
export function useAuditSSE(correlationId) {
    const router = useRouter();
    const authStore = useAuthStore();
    const auditStore = useAuditStore();
    const connect = () => {
        const token = authStore.accessToken;
        if (!token) {
            console.warn('Cannot start event polling: No access token available');
            return;
        }
        // Extract correlationId from ref if it's a ref, otherwise use as-is
        const id = correlationId && typeof correlationId === 'object' && 'value' in correlationId
            ? correlationId.value
            : correlationId;
        if (!id) {
            console.warn('Cannot start event polling: No correlationId available');
            return;
        }
        sseService.connect({
            correlationId: id,
            authToken: token,
            pollInterval: 2000, // Poll every 2 seconds
            maxPollAttempts: 25, // Max 50 seconds (25 * 2s = 50s) for QA cold starts
            onOpen: () => {
                auditStore.setSSEConnected(true);
                auditStore.setLookupProgress('lookup-in-progress');
                console.info('Event polling started for audit events');
            },
            onClose: () => {
                auditStore.setSSEConnected(false);
                console.info('Event polling stopped');
            },
            onTimeout: () => {
                auditStore.setSSEConnected(false);
                auditStore.setLookupProgress('idle');
                auditStore.setError('Animal lookup timed out. The animal may not exist in the system or the service is unavailable. Please try again.');
                console.warn('Animal lookup timed out after polling');
            },
            onError: async (error) => {
                const eventError = error instanceof ErrorEvent ? error.error : error;
                if (isUnauthorizedError(eventError)) {
                    auditStore.setSSEConnected(false);
                    authStore.clearAuth();
                    sseService.disconnect();
                    await redirectToLogin(router, router.currentRoute.value.fullPath);
                    return;
                }
                auditStore.setSSEConnected(false);
                auditStore.setLookupProgress('idle');
                auditStore.setError(`Event polling error: ${error.type}`);
                console.error('Event polling error', error);
            },
            onMessage: (message) => {
                if (message.type === 'rapid-audit-animal-lookup-completed') {
                    const data = message.data;
                    auditStore.setLookupProgress('loading-details');
                    console.log('🎯 About to call setLookupCompleted', {
                        scannedBarcode: data.rapidTagBarcode,
                        expectedCount: data.expectedAnimals?.length || 0,
                    });
                    auditStore.setLookupCompleted(data);
                    console.log('🎯 After calling setLookupCompleted');
                    console.info('Received animal lookup completed event', {
                        correlationId: data.correlationId,
                        cageId: data.cageId || data.cage?.id,
                        expectedAnimalsCount: data.expectedAnimals?.length || 0,
                    });
                }
                else if (message.type === 'connected' || message.type === 'pending') {
                    auditStore.setLookupProgress('lookup-in-progress');
                    console.debug('Event polling status', message.type, message.data);
                }
            },
        });
    };
    const disconnect = () => {
        sseService.disconnect();
        auditStore.setSSEConnected(false);
    };
    const reconnect = () => {
        disconnect();
        connect();
    };
    // Watch for correlationId changes and connect when available
    if (correlationId && typeof correlationId === 'object' && 'value' in correlationId) {
        watch(() => correlationId.value, (newId, oldId) => {
            // Connect when correlationId becomes available (changes from null/undefined to a value)
            if (newId &&
                !oldId &&
                authStore.isAuthenticated &&
                authStore.isTokenValid &&
                !sseService.connected) {
                connect();
            }
        });
    }
    // Auto-connect on mount if authenticated and correlationId is already available
    onMounted(() => {
        // Only auto-connect if we have a correlationId (for pages that already have one)
        const id = correlationId && typeof correlationId === 'object' && 'value' in correlationId
            ? correlationId.value
            : correlationId;
        if (authStore.isAuthenticated && authStore.isTokenValid && id) {
            connect();
        }
    });
    // Auto-disconnect on unmount
    onUnmounted(() => {
        disconnect();
    });
    return {
        connect,
        disconnect,
        reconnect,
        connected: () => sseService.connected,
    };
}
//# sourceMappingURL=useAuditSSE.js.map